import React, { useEffect, useState } from "react";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/Visibility";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import { WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import {
  TesaProductSample,
  ProductSampleReference,
  ProductReferenceRequest,
} from "../../../api/models/product/ProductSampleReference";
import ProductSampleDTO from "../../../api/models/product/ProductSampleDTO";

interface TesaProductSampleOverviewTableProps {
  onDelete: (tesaProductSample: Partial<TesaProductSample>) => void;
  onEdit: (
    productSampleId: number,
    additionalActions?: (sampleData: ProductSampleDTO, usedIn: boolean) => void,
  ) => void;
  productReferences: ProductReferenceRequest[];
  productSampleReferences: ProductSampleReference[];
  showProductSampleDeleteAction: (data: { id: number }) => boolean;
  showProductSampleEditAction: (data: { id: number }) => boolean;
  showProductSampleViewDetailsAction: (data: { id: number }) => boolean;
}

function isTesaProductSampleReference(
  sampleRef: ProductSampleReference,
): sampleRef is ProductSampleReference<TesaProductSample> {
  return sampleRef.productSample.type === "TESA_PRODUCT";
}

const TesaProductSampleOverviewTable: React.FC<TesaProductSampleOverviewTableProps> = (props) => {
  const {
    onDelete,
    onEdit: handleEditById,
    productReferences,
    productSampleReferences,
    showProductSampleDeleteAction,
    showProductSampleEditAction,
    showProductSampleViewDetailsAction,
  } = props;

  const [tesaProductSampleReferences, setTesaProductSampleReferences] = useState<
    ProductSampleReference<TesaProductSample>[]
  >([]);

  const handleEdit = (rowData: CustomTableRowData) => {
    if (showProductSampleEditAction(rowData) || showProductSampleViewDetailsAction(rowData)) {
      handleEditById(rowData.id);
    }
  };

  const rowsTemplate = () => {
    return tesaProductSampleReferences.map((productSampleRef, index) => {
      const { usedIn, productSample } = productSampleRef;

      const productSampleName = usedIn ? (
        <WithPredefinedChip text={productSample.productSampleName} />
      ) : (
        productSample.productSampleName
      );

      return {
        productSampleName,
        nart: productSample.nart,
        productName: productSample.productName,
        baseProduct: productSample.baseProduct,
        claimedProduct: productSample.claimedProduct,
        deliveredBy: formatIdentifierAsString(productSample.deliveredBy),
        id: productSample.id,
        rowNumber: index + 1,
        usedIn,
      };
    });
  };

  useEffect(() => {
    const newSampleRefs = productSampleReferences.filter((productSampleRef: ProductSampleReference) =>
      isTesaProductSampleReference(productSampleRef),
    );
    if (JSON.stringify(newSampleRefs) !== JSON.stringify(tesaProductSampleReferences)) {
      setTesaProductSampleReferences(newSampleRefs as ProductSampleReference<TesaProductSample>[]);
    }
  }, [productSampleReferences]);

  if (!tesaProductSampleReferences) {
    return <></>;
  }

  return (
    <CustomTable
      boldIndex={0}
      selectedItemNameValue="productSampleName"
      excludedKeys={["id", "usedIn"]}
      actionColumnIndexes={[0]}
      handleEdit={handleEdit}
      hasRowNumbers
      hasFixedColumns
      header="tesa Product Samples"
      columns={["Product Sample Name", "NART", "Product Name", "Base Product", "Claimed Product", "Delivered by"]}
      rowMenuActions={[
        {
          action: (rowData): void => handleEditById(rowData.id),
          icon: <EditIcon />,
          text: "Edit",
          show: showProductSampleEditAction,
        },
        {
          action: (rowData): void => handleEditById(rowData.id),
          icon: <ViewIcon />,
          text: "View Details",
          show: showProductSampleViewDetailsAction,
        },
        {
          action: (rowData): void => onDelete(rowData),
          icon: <DeleteIcon />,
          text: "Delete",
          show: showProductSampleDeleteAction,
        },
      ]}
      rowMenus
      rows={rowsTemplate()}
    />
  );
};

export default TesaProductSampleOverviewTable;
